import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import FileCard from '../components/FileCard';
import FilePreview from '../components/FilePreview';
import { Loading, Empty } from '../components/States';
import { useSettings } from '../contexts/SettingsContext';

const PAGE_SIZE = 30;

function FilesPage({ origin = 'local' }) {
  const { userId, hostname } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const untilId = searchParams.get('before') || undefined;
  const sinceId = untilId ? undefined : searchParams.get('after') || undefined;
  const type = searchParams.get('type') || undefined;
  const instanceUrl = (localStorage.getItem('instanceUrl') || 'https://misskey.io').replace(/\/+$/, '');
  const { onlyNsfw, cardSize } = useSettings();
  const gridClass = {
    compact: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6',
    standard: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
    large: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  }[cardSize];
  const scopeKey = JSON.stringify([instanceUrl, origin, userId, hostname, type]);
  const pagerRef = useRef(null);
  const preloadedPage = useRef(null);
  const previewRequest = useRef(null);
  const previewFocus = useRef(null);
  const [fileList, setFileList] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [pageCursors, setPageCursors] = useState({ untilId, sinceId });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const closePreview = useCallback((file) => {
    previewRequest.current?.abort();
    previewRequest.current = null;
    previewFocus.current = file?.id || null;
    setPreview(null);
  }, []);

  const loadPage = useCallback(async (cursors, signal) => {
    const response = await fetch(instanceUrl + '/api/admin/drive/files', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify({
        limit: PAGE_SIZE + 1,
        i: localStorage.getItem('authToken'),
        origin,
        userId,
        hostname,
        type,
        ...cursors,
      }),
    });
    if (!response.ok) throw new Error('Failed to load files.');
    const data = await response.json();
    if (!Array.isArray(data)) throw new Error('Invalid file list.');
    const page = data.slice(0, PAGE_SIZE);
    // sinceId returns the nearest newer files in ascending order.
    return { files: cursors.sinceId ? page.reverse() : page, hasMore: data.length > PAGE_SIZE };
  }, [instanceUrl, origin, userId, hostname, type]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const cached = preloadedPage.current;
    const page = cached?.scopeKey === scopeKey && cached.untilId === untilId && cached.sinceId === sinceId ? cached.page : null;
    if (!page) closePreview();
    setLoading(true);
    setError('');

    const loadFiles = async () => {
      try {
        const result = page || await loadPage({ untilId, sinceId }, controller.signal);
        if (!active) return;
        setFileList(result.files);
        setHasMore(result.hasMore);
        setPageCursors({ untilId, sinceId });
      } catch (error) {
        if (active && error.name !== 'AbortError') {
          console.error('Error:', error);
          setFileList([]);
          setHasMore(false);
          setError('Could not load files. Please reload this page.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadFiles();
    return () => {
      active = false;
      controller.abort();
    };
  }, [loadPage, untilId, sinceId, scopeKey, closePreview]);

  useEffect(() => {
    closePreview();
  }, [onlyNsfw, closePreview]);

  useEffect(() => () => {
    previewRequest.current?.abort();
    previewRequest.current = null;
  }, []);

  useEffect(() => {
    if (preview || !previewFocus.current) return;
    const id = previewFocus.current;
    previewFocus.current = null;
    const card = Array.from(document.querySelectorAll('[data-file-id]')).find(element => element.dataset.fileId === id);
    if (!card) return;
    if (!card.contains(document.activeElement)) card.focus({ preventScroll: true });
    card.scrollIntoView?.({ block: 'center', inline: 'nearest' });
  }, [preview]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (document.querySelector('dialog[open]')) return;
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.target instanceof Element && event.target.closest('input, textarea, select, audio, video, [contenteditable]:not([contenteditable="false"]), [role="slider"]')) return;

      const direction = { ArrowLeft: 'previous', ArrowRight: 'next' }[event.key];
      if (!direction) return;
      const button = pagerRef.current?.querySelector('[data-page="' + direction + '"]');
      if (button && !button.disabled) {
        event.preventDefault();
        button.click();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const changePage = (direction, fileId) => {
    const params = new URLSearchParams(searchParams);
    params.delete('before');
    params.delete('after');
    if (direction && fileId) params.set(direction, fileId);
    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  const hasPrevious = Boolean(pageCursors.untilId) || (Boolean(pageCursors.sinceId) && hasMore);
  const hasNext = Boolean(pageCursors.sinceId) || hasMore;
  const pageMatchesUrl = pageCursors.untilId === untilId && pageCursors.sinceId === sinceId;
  const displayedFiles = onlyNsfw ? fileList.filter(file => file.isSensitive) : fileList;

  const openPreview = (file) => {
    if (!loading && pageMatchesUrl) setPreview({ file, loading: false, error: '', atStart: false, atEnd: false });
  };

  const movePreview = async (direction) => {
    if (!preview || previewRequest.current || loading || !pageMatchesUrl) return;
    const index = displayedFiles.findIndex(file => file.id === preview.file.id);
    const next = displayedFiles[index + direction];
    if (next) {
      setPreview(current => ({ ...current, file: next, error: '' }));
      return;
    }
    if (direction < 0 ? !hasPrevious || preview.atStart : !hasNext || preview.atEnd) return;

    const controller = new AbortController();
    previewRequest.current = controller;
    setPreview(current => ({ ...current, loading: true, error: '' }));
    let cursor = direction > 0 ? fileList[fileList.length - 1]?.id : fileList[0]?.id;
    try {
      while (cursor) {
        const cursors = direction > 0 ? { untilId: cursor } : { sinceId: cursor };
        const page = await loadPage(cursors, controller.signal);
        if (controller.signal.aborted || previewRequest.current !== controller) return;
        const visible = onlyNsfw ? page.files.filter(file => file.isSensitive) : page.files;
        if (visible.length) {
          // Reuse the fetched page when the URL changes below.
          preloadedPage.current = { scopeKey, untilId: cursors.untilId, sinceId: cursors.sinceId, page };
          setFileList(page.files);
          setHasMore(page.hasMore);
          setPageCursors(cursors);
          setPreview(current => ({
            ...current,
            file: direction > 0 ? visible[0] : visible[visible.length - 1],
            loading: false,
            error: '',
            atStart: false,
            atEnd: false,
          }));
          changePage(direction > 0 ? 'before' : 'after', cursor);
          return;
        }
        if (!page.hasMore || page.files.length === 0) {
          setPreview(current => ({ ...current, [direction > 0 ? 'atEnd' : 'atStart']: true }));
          return;
        }
        const nextCursor = direction > 0 ? page.files[page.files.length - 1].id : page.files[0].id;
        if (!nextCursor || nextCursor === cursor) throw new Error('File page cursor did not advance.');
        cursor = nextCursor;
      }
    } catch (error) {
      if (!controller.signal.aborted && error.name !== 'AbortError') {
        console.error('Error loading preview page:', error);
        setPreview(current => current && ({ ...current, error: '前後のファイルを読み込めませんでした。もう一度お試しください。' }));
      }
    } finally {
      if (previewRequest.current === controller) {
        previewRequest.current = null;
        setPreview(current => current && ({ ...current, loading: false }));
      }
    }
  };

  const title = userId ? 'User Files' : hostname ? 'Server Files: ' + hostname
    : origin === 'combined' ? 'All Files' : origin === 'remote' ? 'Remote Files' : 'Local Files';

  return (
    <div className="px-4 sm:px-6 lg:px-8" aria-busy={loading}>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <div className="flex items-center gap-3">
          {(untilId || sinceId) && (
            <button className="text-sm text-blue-600 hover:underline" onClick={() => changePage()} disabled={loading}>Latest files</button>
          )}
          <div className="text-sm text-gray-500 bg-white px-3 py-1 rounded-full shadow-sm border border-gray-100">
            Showing {displayedFiles.length} files
          </div>
        </div>
      </div>
      {loading && (fileList.length === 0 ? <Loading /> : <p role="status">Loading files...</p>)}
      {error && <p role="alert">{error}</p>}
      {!loading && !error && displayedFiles.length === 0 && (
        <Empty message={fileList.length ? 'No matching files on this page.' : 'No files found.'} />
      )}
      <div data-testid="file-grid" className={`grid gap-4 sm:gap-6 ${gridClass}`}>
        {displayedFiles.map(file => <FileCard key={file.id} filedata={file} onPreview={openPreview} />)}
      </div>
      <div ref={pagerRef} className="fixed z-20 bottom-6 left-1/2 -translate-x-1/2 flex bg-white/80 backdrop-blur-md rounded-full shadow-xl border border-gray-200 p-1">
        <button
          data-page="previous"
          className="px-6 py-2.5 rounded-full text-sm font-medium transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading || !pageMatchesUrl || !hasPrevious || fileList.length === 0}
          onClick={() => changePage('after', fileList[0].id)}
        >Previous</button>
        <div className="w-px bg-gray-200 my-2 mx-1"></div>
        <button
          data-page="next"
          className="px-6 py-2.5 rounded-full text-sm font-medium transition-colors text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading || !pageMatchesUrl || !hasNext || fileList.length === 0}
          onClick={() => changePage('before', fileList[fileList.length - 1].id)}
        >Next</button>
      </div>
      {preview && (
        <FilePreview file={preview.file} files={displayedFiles} onClose={closePreview} onNavigate={movePreview}
          hasPrevious={hasPrevious && !preview.atStart} hasNext={hasNext && !preview.atEnd}
          loading={preview.loading || loading || !pageMatchesUrl} error={preview.error} />
      )}
    </div>
  );
}

export default FilesPage;
